import { useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router'
import {
  ArrowLeft,
  Play,
  Trash2,
  Search,
  Phone,
  FileText,
  AlertTriangle,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import {
  useCampaignList,
  useCampaignContacts,
  useCampaignDuplicates,
  useDeleteCampaignList,
  type CampaignContact,
} from '../api/campaigns'
import { DuplicatesPanel } from '../components/campaigns/DuplicatesPanel'
import { Pagination } from '../components/shared/Pagination'
import { SpinLoader } from '../components/shared/SpinLoader'
import { ErrorCard } from '../components/shared/ErrorCard'
import { useDispositionLabel } from '../i18n/dispositionLabels'
import { useStatusLabel } from '../i18n/statusLabels'
import { formatPhone, formatDateTime, cn } from '../lib/utils'
import { useDebouncedValue } from '../hooks/useDebounce'

const PAGE_SIZE = 20

const STATUS_TABS = [
  { labelKey: 'detail.statusTabs.all', value: undefined },
  { labelKey: 'detail.statusTabs.pending', value: 'pending' },
  { labelKey: 'detail.statusTabs.called', value: 'called' },
  { labelKey: 'detail.statusTabs.skipped', value: 'skipped' },
] as const

export function CampaignDetailPage() {
  const { t } = useTranslation('campaigns')
  const { id } = useParams()
  const listId = id ? Number(id) : undefined
  const navigate = useNavigate()

  const { data: campaign, isLoading: campaignLoading, refetch } = useCampaignList(listId)
  const deleteMutation = useDeleteCampaignList()

  const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined)
  const [offset, setOffset] = useState(0)
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebouncedValue(search, 300)
  const [showDuplicates, setShowDuplicates] = useState(false)

  const { data: contactsData, isLoading: contactsLoading, isError } = useCampaignContacts(
    listId,
    { limit: PAGE_SIZE, offset, status: statusFilter },
  )
  const { data: duplicatesData } = useCampaignDuplicates(listId)

  const handleSearch = (value: string) => {
    setSearch(value)
  }

  const handleDelete = () => {
    if (!campaign) return
    if (confirm(t('list.deleteConfirm', { name: campaign.name, count: campaign.total_contacts }))) {
      deleteMutation.mutate(campaign.id, {
        onSuccess: () => navigate('/app/campaigns'),
      })
    }
  }

  const handleStatusChange = (status: string | undefined) => {
    setStatusFilter(status)
    setOffset(0)
  }

  if (!listId) {
    navigate('/app/campaigns')
    return null
  }

  // Client-side search filter on already-loaded contacts
  const contacts = contactsData?.contacts ?? []
  const filteredContacts = debouncedSearch
    ? contacts.filter(
        (c) =>
          c.name.toLowerCase().includes(debouncedSearch.toLowerCase()) ||
          c.phone.includes(debouncedSearch),
      )
    : contacts
  const total = debouncedSearch ? filteredContacts.length : (contactsData?.total ?? 0)

  const duplicateGroups = duplicatesData?.groups ?? []
  const hasDuplicates = duplicateGroups.length > 0

  const stats = campaign?.stats
  const pct = stats && stats.total > 0 ? Math.round((stats.contacted / stats.total) * 100) : 0
  const isComplete = stats && stats.remaining === 0 && stats.total > 0

  return (
    <div className="space-y-5">
      {/* Back link */}
      <Link
        to="/app/campaigns"
        className="inline-flex items-center gap-1.5 text-sm text-text-secondary transition-colors hover:text-primary"
      >
        <ArrowLeft className="h-4 w-4" />
        {t('detail.back')}
      </Link>

      {/* Header */}
      {campaignLoading && <SpinLoader label={t('detail.loading')} />}
      {campaign && (
        <div className="rounded-lg border border-border bg-surface-card p-5">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <h1 className="text-xl font-bold text-text-primary md:text-2xl">
                {campaign.name}
              </h1>
              <p className="mt-1 text-sm text-text-muted">
                {campaign.file_name} · {t('detail.contactsCount', { count: campaign.total_contacts })}
                {campaign.description && ` · ${campaign.description}`}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => navigate(`/app/campaigns/${listId}/run`)}
                className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-primary/90"
              >
                <Play className="h-4 w-4" />
                {t('detail.startCalling')}
              </button>
              <button
                onClick={handleDelete}
                className="rounded-lg border border-border p-2.5 text-text-muted transition-colors hover:border-error/50 hover:bg-error/10 hover:text-error"
                title={t('list.deleteCampaign')}
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Progress bar */}
          {stats && (
            <div className="mt-4">
              <div className="h-2.5 w-full overflow-hidden rounded-full bg-surface-elevated">
                <div
                  className={cn(
                    'h-full rounded-full transition-all duration-500',
                    isComplete ? 'bg-success' : 'bg-primary',
                  )}
                  style={{ width: `${pct}%` }}
                />
              </div>
              <div className="mt-2 flex items-center justify-between text-xs text-text-muted">
                <span>{t('detail.calledPct', { contacted: stats.contacted, total: stats.total, pct })}</span>
                <span>
                  {isComplete ? (
                    <span className="font-medium text-success">{t('list.complete')}</span>
                  ) : (
                    t('list.remaining', { count: stats.remaining })
                  )}
                </span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Duplicate warning banner */}
      {hasDuplicates && !showDuplicates && (
        <button
          onClick={() => setShowDuplicates(true)}
          className="flex w-full items-center gap-3 rounded-lg border border-warning/30 bg-warning/5 px-4 py-3 text-left transition-colors hover:bg-warning/10"
        >
          <AlertTriangle className="h-5 w-5 shrink-0 text-warning" />
          <div className="flex-1">
            <span className="text-sm font-medium text-text-primary">
              {t('detail.duplicates', { count: duplicateGroups.length })}
            </span>
            <span className="ml-2 text-xs text-text-muted">
              {t('detail.duplicatesContacts', { count: duplicatesData?.total_duplicates ?? 0 })}
            </span>
          </div>
          <span className="text-xs font-medium text-warning">{t('detail.view')}</span>
        </button>
      )}

      {/* Duplicates panel */}
      {showDuplicates && hasDuplicates && listId && (
        <DuplicatesPanel
          groups={duplicateGroups}
          totalDuplicates={duplicatesData?.total_duplicates ?? 0}
          listId={listId}
          onResolved={() => {
            refetch()
          }}
        />
      )}

      {/* Search + Filters */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="relative flex-1 sm:max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-muted" />
          <input
            type="text"
            value={search}
            onChange={(e) => handleSearch(e.target.value)}
            placeholder={t('detail.search')}
            className="w-full rounded-lg border border-border bg-surface py-2 pl-10 pr-4 text-sm text-text-primary placeholder:text-text-muted focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
          />
        </div>
        <div className="flex gap-1">
          {STATUS_TABS.map((tab) => (
            <button
              key={tab.labelKey}
              onClick={() => handleStatusChange(tab.value)}
              className={cn(
                'rounded-lg px-3 py-1.5 text-xs font-medium transition-colors',
                statusFilter === tab.value
                  ? 'bg-primary text-white'
                  : 'bg-surface-elevated text-text-secondary hover:text-text-primary',
              )}
            >
              {t(tab.labelKey)}
            </button>
          ))}
        </div>
      </div>

      {/* Loading / Error */}
      {contactsLoading && <SpinLoader label={t('detail.loadingContacts')} />}
      {isError && <ErrorCard message={t('detail.loadFailed')} onRetry={() => refetch()} />}

      {/* Empty state */}
      {!contactsLoading && filteredContacts.length === 0 && (
        <div className="rounded-lg border border-border bg-surface-card p-8 text-center">
          <FileText className="mx-auto h-10 w-10 text-text-muted" />
          <p className="mt-3 text-sm text-text-muted">
            {debouncedSearch
              ? t('detail.empty.noMatch')
              : statusFilter
                ? t('detail.empty.noStatus', { status: statusFilter })
                : t('detail.empty.noContacts')}
          </p>
        </div>
      )}

      {/* Contacts table (desktop) */}
      {filteredContacts.length > 0 && (
        <>
          <div className="hidden overflow-x-auto rounded-lg border border-border bg-surface-card md:block">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-xs text-text-muted">
                  <th className="px-4 py-3 w-12">{t('detail.table.index')}</th>
                  <th className="px-4 py-3">{t('detail.table.name')}</th>
                  <th className="px-4 py-3">{t('detail.table.phone')}</th>
                  <th className="px-4 py-3">{t('detail.table.status')}</th>
                  <th className="px-4 py-3">{t('detail.table.disposition')}</th>
                  <th className="px-4 py-3">{t('detail.table.calledAt')}</th>
                </tr>
              </thead>
              <tbody>
                {filteredContacts.map((contact, i) => (
                  <ContactRow
                    key={contact.id}
                    contact={contact}
                    index={offset + i + 1}
                  />
                ))}
              </tbody>
            </table>
          </div>

          {/* Contacts cards (mobile) */}
          <div className="space-y-2 md:hidden">
            {filteredContacts.map((contact, i) => (
              <ContactCard
                key={contact.id}
                contact={contact}
                index={offset + i + 1}
              />
            ))}
          </div>

          {!debouncedSearch && (
            <Pagination offset={offset} limit={PAGE_SIZE} total={total} onPageChange={setOffset} />
          )}
        </>
      )}
    </div>
  )
}

function ContactRow({ contact, index }: { contact: CampaignContact; index: number }) {
  const { t } = useTranslation('campaigns')
  const statusLabel = useStatusLabel(contact.status, 'campaignContact')
  const dispositionLabel = useDispositionLabel(contact.disposition ?? '')
  return (
    <tr className="border-b border-border/50 transition-colors hover:bg-surface-elevated/50">
      <td className="px-4 py-3 text-text-muted">{index}</td>
      <td className="px-4 py-3 font-medium text-text-primary">
        {contact.name || <span className="italic text-text-muted">{t('detail.table.noName')}</span>}
      </td>
      <td className="px-4 py-3">
        <span className="inline-flex items-center gap-1.5 font-mono text-text-secondary">
          <Phone className="h-3.5 w-3.5" />
          {formatPhone(contact.phone)}
        </span>
      </td>
      <td className="px-4 py-3">
        <span
          className={cn(
            'inline-block rounded-full px-2 py-0.5 text-xs font-medium',
            contact.status === 'called'
              ? 'bg-success/10 text-success'
              : contact.status === 'skipped'
                ? 'bg-text-muted/10 text-text-muted'
                : 'bg-primary/10 text-primary',
          )}
        >
          {statusLabel}
        </span>
      </td>
      <td className="px-4 py-3 text-text-secondary">
        {contact.disposition ? dispositionLabel : <span className="text-text-muted">—</span>}
      </td>
      <td className="px-4 py-3 text-text-muted">
        {contact.called_at ? formatDateTime(contact.called_at) : '—'}
      </td>
    </tr>
  )
}

function ContactCard({ contact, index }: { contact: CampaignContact; index: number }) {
  const { t } = useTranslation('campaigns')
  const statusLabel = useStatusLabel(contact.status, 'campaignContact')
  const dispositionLabel = useDispositionLabel(contact.disposition ?? '')
  return (
    <div className="rounded-lg border border-border bg-surface-card p-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-xs text-text-muted">#{index}</span>
          <span className="font-medium text-text-primary">
            {contact.name || <span className="italic text-text-muted">{t('detail.card.noName')}</span>}
          </span>
        </div>
        <span
          className={cn(
            'inline-block rounded-full px-2 py-0.5 text-xs font-medium',
            contact.status === 'called'
              ? 'bg-success/10 text-success'
              : contact.status === 'skipped'
                ? 'bg-text-muted/10 text-text-muted'
                : 'bg-primary/10 text-primary',
          )}
        >
          {statusLabel}
        </span>
      </div>
      <div className="mt-1 flex items-center gap-1.5 font-mono text-sm text-text-secondary">
        <Phone className="h-3.5 w-3.5" />
        {formatPhone(contact.phone)}
      </div>
      {contact.disposition && (
        <p className="mt-1 text-xs text-text-secondary">
          {t('detail.card.dispositionLabel', { value: dispositionLabel })}
        </p>
      )}
      {contact.called_at && (
        <p className="mt-0.5 text-xs text-text-muted">
          {t('detail.card.calledLabel', { date: formatDateTime(contact.called_at) })}
        </p>
      )}
    </div>
  )
}
